import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import {
  Box,
  Button,
  ButtonGroup,
  Paper,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Layout, SpawnMode } from "@/features/simulation/public";
import {
  LAYOUT_BOUNDS,
  PARAM_BOUNDS,
  pressPositionRange,
  type SpeedMultiplier,
  useSimulationControls,
} from "@/features/simulation/public";
import { clampInt } from "@/lib/math/clamp";

/**
 * 下部コントロールパネル。
 * レイアウト設定（長さ・プレス位置）、4 パラメータ、投入モード、実行制御を持つ。
 * 全てのパラメータは整数刻み。
 *
 * MUI 9 で Stack/Typography の system shortcut props（alignItems / fontWeight 等）と
 * TextField の inputProps が削除されたため、本ファイルでは sx と slotProps.htmlInput を
 * 統一的に使用する。
 */
export function ControlsPanel() {
  const {
    params,
    layout,
    spawnMode,
    running,
    speed,
    queuedSpawns,
    start,
    pause,
    reset,
    setParams,
    setLayout,
    setSpawnMode,
    setSpeed,
    requestSpawn,
  } = useSimulationControls();

  const pressRange = pressPositionRange(layout.conveyorLength);

  return (
    <Paper
      elevation={6}
      sx={{
        borderRadius: 0,
        borderTop: "1px solid",
        borderColor: "divider",
        p: 1.5,
        pr: 2,
        pl: 2,
        background: "#ffffff",
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: "stretch", flexWrap: "wrap" }}>
        {/* 実行制御 */}
        <Stack spacing={0.5} sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            実行制御
          </Typography>
          <Stack direction="row" spacing={1}>
            {running ? (
              <Button variant="contained" color="warning" startIcon={<PauseRoundedIcon />} onClick={pause}>
                一時停止
              </Button>
            ) : (
              <Button variant="contained" color="primary" startIcon={<PlayArrowRoundedIcon />} onClick={start}>
                開始
              </Button>
            )}
            <Tooltip title="すべてリセット">
              <Button variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={reset}>
                リセット
              </Button>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, alignItems: "center" }}>
            <Typography variant="caption" color="text.secondary">
              速度
            </Typography>
            <ButtonGroup size="small">
              {[1, 2, 4].map((s) => (
                <Button
                  key={s}
                  variant={speed === s ? "contained" : "outlined"}
                  onClick={() => setSpeed(s as SpeedMultiplier)}
                  sx={{ minWidth: 36 }}
                >
                  ×{s}
                </Button>
              ))}
            </ButtonGroup>
          </Stack>
          {/* バッファに空きが出ると自動で再開するため、手動再開ボタンは廃止 */}
        </Stack>

        <Divider />

        {/* レイアウト（変更で自動リセット） */}
        <Stack spacing={0.5} sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            レイアウト
            <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
              ※ 変更でリセット
            </Typography>
          </Typography>
          <IntParamRow
            label="コンベア長"
            unit="タイル"
            value={layout.conveyorLength}
            min={LAYOUT_BOUNDS.conveyorLength.min}
            max={LAYOUT_BOUNDS.conveyorLength.max}
            onChange={(v) => setLayout({ conveyorLength: v } satisfies Partial<Layout>)}
          />
          <IntParamRow
            label="プレス位置"
            unit={`(${pressRange.min}–${pressRange.max})`}
            value={layout.pressPosition}
            min={pressRange.min}
            max={pressRange.max}
            onChange={(v) => setLayout({ pressPosition: v } satisfies Partial<Layout>)}
          />
          <IntParamRow
            label="バッファ上限"
            unit="個"
            value={layout.bufferCapacity}
            min={LAYOUT_BOUNDS.bufferCapacity.min}
            max={LAYOUT_BOUNDS.bufferCapacity.max}
            onChange={(v) => setLayout({ bufferCapacity: v } satisfies Partial<Layout>)}
          />
          <IntParamRow
            label="搬送積載上限"
            unit="個"
            value={layout.transportCapacity}
            min={LAYOUT_BOUNDS.transportCapacity.min}
            max={LAYOUT_BOUNDS.transportCapacity.max}
            onChange={(v) => setLayout({ transportCapacity: v } satisfies Partial<Layout>)}
          />
        </Stack>

        <Divider />

        {/* パラメータ入力（整数刻み） */}
        <ParamSlider
          label="コンベア搬送速度"
          unit="タイル/秒"
          value={params.conveyorSpeed}
          min={PARAM_BOUNDS.conveyorSpeed.min}
          max={PARAM_BOUNDS.conveyorSpeed.max}
          onChange={(v) => setParams({ conveyorSpeed: v })}
        />
        <ParamSlider
          label="プレス加工時間"
          unit="秒"
          value={params.pressProcessingTime}
          min={PARAM_BOUNDS.pressProcessingTime.min}
          max={PARAM_BOUNDS.pressProcessingTime.max}
          onChange={(v) => setParams({ pressProcessingTime: v })}
        />
        <ParamSlider
          label="アーム作業時間"
          unit="秒"
          value={params.armTime}
          min={PARAM_BOUNDS.armTime.min}
          max={PARAM_BOUNDS.armTime.max}
          onChange={(v) => setParams({ armTime: v })}
        />
        <ParamSlider
          label="搬送ロボ速度"
          unit="タイル/秒"
          value={params.transportSpeed}
          min={PARAM_BOUNDS.transportSpeed.min}
          max={PARAM_BOUNDS.transportSpeed.max}
          onChange={(v) => setParams({ transportSpeed: v })}
        />

        <Divider />

        {/* ワーク投入モード */}
        <Stack spacing={0.5} sx={{ minWidth: 240 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            ワーク投入モード
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={spawnMode.kind}
            onChange={(_, value) => {
              if (!value) return;
              if (value === "auto") setSpawnMode({ kind: "auto", intervalSec: 2 });
              if (value === "manual") setSpawnMode({ kind: "manual" });
              if (value === "queued") setSpawnMode({ kind: "queued", maxN: 10 });
            }}
          >
            <ToggleButton value="auto">自動</ToggleButton>
            <ToggleButton value="manual">手動</ToggleButton>
            <ToggleButton value="queued">キュー</ToggleButton>
          </ToggleButtonGroup>
          <SpawnModeDetails
            mode={spawnMode}
            queuedSpawns={queuedSpawns}
            onChange={setSpawnMode}
            onRequestSpawn={requestSpawn}
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

function Divider() {
  return <Box sx={{ borderLeft: "1px solid", borderColor: "divider", mx: 0.5 }} />;
}

interface ParamSliderProps {
  label: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}

function ParamSlider({ label, unit, value, min, max, onChange }: ParamSliderProps) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 150 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <TextField
          type="number"
          size="small"
          value={value}
          slotProps={{ htmlInput: { min, max, step: 1, style: { width: 48 } } }}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!Number.isFinite(v)) return;
            onChange(clampInt(v, min, max));
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {unit}
        </Typography>
      </Stack>
      <Slider
        size="small"
        value={value}
        min={min}
        max={max}
        step={1}
        marks
        onChange={(_, v) => {
          if (typeof v === "number") onChange(clampInt(v, min, max));
        }}
      />
    </Stack>
  );
}

interface IntParamRowProps {
  label: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}

/** レイアウト用: スライダー無しのコンパクトな整数入力 */
function IntParamRow({ label, unit, value, min, max, onChange }: IntParamRowProps) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 84 }}>
        {label}
      </Typography>
      <TextField
        type="number"
        size="small"
        value={value}
        slotProps={{ htmlInput: { min, max, step: 1, style: { width: 56 } } }}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (!Number.isFinite(v)) return;
          onChange(clampInt(v, min, max));
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {unit}
      </Typography>
    </Stack>
  );
}

interface SpawnModeDetailsProps {
  mode: SpawnMode;
  queuedSpawns: number;
  onChange: (mode: SpawnMode) => void;
  onRequestSpawn: () => void;
}

function SpawnModeDetails({ mode, queuedSpawns, onChange, onRequestSpawn }: SpawnModeDetailsProps) {
  if (mode.kind === "auto") {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="caption" color="text.secondary">
          間隔
        </Typography>
        <TextField
          type="number"
          size="small"
          value={mode.intervalSec}
          slotProps={{
            htmlInput: {
              min: PARAM_BOUNDS.autoSpawnInterval.min,
              max: PARAM_BOUNDS.autoSpawnInterval.max,
              step: 1,
              style: { width: 56 },
            },
          }}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!Number.isFinite(v)) return;
            const n = clampInt(v, PARAM_BOUNDS.autoSpawnInterval.min, PARAM_BOUNDS.autoSpawnInterval.max);
            onChange({ kind: "auto", intervalSec: n });
          }}
        />
        <Typography variant="caption" color="text.secondary">
          秒/個
        </Typography>
      </Stack>
    );
  }
  if (mode.kind === "manual") {
    return (
      <Button
        variant="contained"
        color="secondary"
        startIcon={<AddBoxRoundedIcon />}
        size="small"
        onClick={onRequestSpawn}
      >
        ワーク投入
      </Button>
    );
  }
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <TextField
        type="number"
        size="small"
        label="最大"
        value={mode.maxN}
        slotProps={{
          htmlInput: {
            min: PARAM_BOUNDS.queuedMaxN.min,
            max: PARAM_BOUNDS.queuedMaxN.max,
            step: 1,
            style: { width: 56 },
          },
        }}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (!Number.isFinite(v)) return;
          onChange({
            kind: "queued",
            maxN: clampInt(v, PARAM_BOUNDS.queuedMaxN.min, PARAM_BOUNDS.queuedMaxN.max),
          });
        }}
      />
      <Button
        variant="contained"
        color="secondary"
        startIcon={<AddBoxRoundedIcon />}
        size="small"
        onClick={onRequestSpawn}
      >
        要求
      </Button>
      <Typography variant="caption" color="text.secondary">
        待ち: {queuedSpawns}
      </Typography>
    </Stack>
  );
}
